
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface SetsControlProps {
  sets: number;
  onSetsChange: (value: number) => void;
  isPersonalPlan?: boolean;
  hasUnsyncedChanges?: boolean;
  isSyncInProgress?: boolean;
}

export const SetsControl = ({
  sets,
  onSetsChange,
  isPersonalPlan = false,
  hasUnsyncedChanges = false,
  isSyncInProgress = false
}: SetsControlProps) => {
  const handleDecrease = useCallback(() => {
    onSetsChange(Math.max(1, sets - 1));
  }, [sets, onSetsChange]);

  const handleIncrease = useCallback(() => {
    onSetsChange(sets + 1);
  }, [sets, onSetsChange]);

  const getSyncIndicator = () => {
    if (isSyncInProgress) return '⚡';
    if (hasUnsyncedChanges) return '📤';
    return null;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-black dark:text-white forest:text-white sunset:text-[#ef5609]">
          Series
        </label>
        {isPersonalPlan && (hasUnsyncedChanges || isSyncInProgress) && (
          <div className="flex items-center gap-1">
            <span className="text-xs animate-pulse">
              {getSyncIndicator()}
            </span>
            {isSyncInProgress && (
              <div className="w-2 h-2 bg-[#00C1D6] rounded-full animate-pulse" />
            )}
          </div>
        )}
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleDecrease}
          disabled={isSyncInProgress}
          className={cn(
            "h-8 w-8 p-0 transition-all duration-150",
            "border-[#2196A3]/50 hover:border-[#00C1D6] hover:bg-[#00C1D6]/20",
            "dark:border-white/20 dark:hover:border-[#00C1D6]",
            "forest:border-[#0D170D]/50 forest:hover:border-green-500",
            "sunset:border-orange-500/50 sunset:hover:border-[#ef5609]",
            hasUnsyncedChanges && "ring-1 ring-[#00C1D6]/50",
            isSyncInProgress && "opacity-70 cursor-wait"
          )}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className={cn(
          "min-w-[2rem] text-center font-medium transition-all duration-150",
          "text-black dark:text-white forest:text-white sunset:text-[#ef5609]",
          hasUnsyncedChanges && "text-[#00C1D6] font-semibold",
          isSyncInProgress && "animate-pulse"
        )}>
          {sets}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleIncrease}
          disabled={isSyncInProgress}
          className={cn(
            "h-8 w-8 p-0 transition-all duration-150",
            "border-[#2196A3]/50 hover:border-[#00C1D6] hover:bg-[#00C1D6]/20",
            "dark:border-white/20 dark:hover:border-[#00C1D6]",
            "forest:border-[#0D170D]/50 forest:hover:border-green-500",
            "sunset:border-orange-500/50 sunset:hover:border-[#ef5609]",
            hasUnsyncedChanges && "ring-1 ring-[#00C1D6]/50",
            isSyncInProgress && "opacity-70 cursor-wait"
          )}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
